import type { PageHelp } from "../../help/types";

export const hrPackageEmployeesPageHelp: PageHelp = {
  title: "Package employees",
  intro:
    "This page lists the employees of one benefit package of a client company, with their enrollment lines in that package and those of their dependents. You reach it from a package on the HR page (Open Package Employees). Use it to check who is enrolled, handle pending requests and manage eligibility for this package.",
  sections: [
    "Getting around",
    "Finding employees",
    "Bulk actions",
    "The employee table",
    "Row actions",
    "Enrollments and dependents",
  ],
  contexts: {
    "row-expanded":
      "Click an employee row (or the arrow at the right of the name) to expand it and show the enrollment lines and dependents. Only one row is open at a time.",
  },
  tips: [
    "The table shows only enrollment lines of this package. The employee's other benefits are on the Employees tab of the HR page.",
    "The Only Active switch is shared with the HR page, so changing it here also changes it there.",
    "Most actions need edit rights. With a read-only role the buttons are shown but disabled.",
  ],
  steps: [
    {
      selector: '[data-help="package-employees-breadcrumb-hr"]',
      section: "Getting around",
      title: "Go back to the HR page",
      body: "The HR breadcrumb takes you back to the HR page, with the same company still selected.",
    },
    {
      selector: '[data-help="package-employees-breadcrumb-package"]',
      section: "Getting around",
      title: "Go back to the package list",
      body: "The second breadcrumb shows the name of this package. Click it to open the list of all packages of the same benefit.",
    },
    {
      selector: '[data-help="package-employees-refresh"]',
      section: "Getting around",
      title: "Reload the data",
      body: "Fetches the data from the server again, for example after a colleague or an employee has made changes in the meantime.",
    },
    {
      selector: '[data-help="package-employees-search"]',
      section: "Finding employees",
      title: "Search employees by name",
      body: "Type part of an employee's first or last name. The table filters after a short pause. Email addresses and dependents' names are not searched.",
    },
    {
      selector: '[data-help="package-employees-results"]',
      section: "Finding employees",
      title: "See how many employees are listed",
      body: "Shows how many employees the table currently lists, after the search and the switches below are applied.",
    },
    {
      selector: '[data-help="only-active-switch"]',
      section: "Finding employees",
      title: "Show only enrollments in active benefits",
      body: "Only Active is on by default and shows only enrollments in packages of benefits that are currently active. Turn it off to include enrollments in benefits that have ended. If this package belongs to a benefit that has ended, turn it off to see its enrollments.",
    },
    {
      selector: '[data-help="hr-only-enrolled"]',
      section: "Finding employees",
      title: "Show only employees in this package",
      body: "Only Elgible/Enrolled is on by default and lists only employees and dependents who have an enrollment line in this package. Turn it off to list every employee of the company, for example to make someone eligible for the package.",
    },
    {
      selector: '[data-help="hr-column-select"]',
      section: "Bulk actions",
      title: "Select employees",
      body: "Tick the box in the header to select every employee in the table, or tick single rows. Export Consents and Resend Invitations above the table work on the selected employees.",
    },
    {
      selector: '[data-help="hr-export-consents"]',
      section: "Bulk actions",
      title: "Export consents for the selected employees",
      body: "Exports the consent documents of the selected employees. The button is disabled until you select at least one employee. Tick Is Salary Deduction Consent to export the salary-deduction consents instead of the regular consents. After you click Export, you get an email with a download link.",
    },
    {
      selector: '[data-help="hr-resend-invitations"]',
      section: "Bulk actions",
      title: "Resend the portal invitation",
      body: "Sends the employee-portal invitation email again to every selected employee. The button is disabled until you select at least one employee.",
    },
    {
      selector: '[data-help="hr-employee-name"]',
      section: "The employee table",
      optional: true,
      title: "Expand a row to see the details",
      body: "Click a row to open its enrollment lines in this package and its dependents. A red bell in front of the name means the employee has open items, which can also be outside this package. A green shield means the employee has given the GrECo sensitive-data consent.",
    },
    {
      selector: '[data-help="hr-column-enrollments"]',
      section: "The employee table",
      title: "Count the enrollment lines",
      body: "Enrollments shows how many enrollment lines the employee has in this package. Dependents' enrollment lines are not counted here.",
    },
    {
      selector: '[data-help="hr-column-dependents"]',
      section: "The employee table",
      title: "Count the dependents",
      body: "Dependents shows how many dependents are registered for the employee, whether or not they are in this package.",
    },
    {
      selector: '[data-help="hr-employee-resend-invitation"]',
      section: "Row actions",
      optional: true,
      title: "Resend one invitation",
      body: "Sends the employee-portal invitation email to this employee again. Select the row first: the button stays disabled until then.",
    },
    {
      selector: '[data-help="hr-employee-export-consents"]',
      section: "Row actions",
      optional: true,
      title: "Export one employee's consents",
      body: "Opens Export Consents for this employee only. As with the bulk export, tick Is Salary Deduction Consent to export the salary-deduction consents instead. The download link arrives by email.",
    },
    {
      selector: '[data-help="hr-employee-edit"]',
      section: "Row actions",
      optional: true,
      title: "View or edit the employee",
      body: "Opens the employee form with the employee's data, dependents and documents. A yellow icon means the employee changed data in the employee portal: the changed fields are highlighted in yellow, and Confirm accepts them. Read-only users see the icon disabled.",
    },
    {
      selector: '[data-help="hr-employee-eligibility"]',
      section: "Row actions",
      optional: true,
      title: "Make the employee eligible for this package",
      body: "Opens Manage Package Eligibility with this package already selected. The same button on a dependent's line does this for the dependent. It is disabled when the person already has an enrollment line in this package, and for read-only users.",
    },
    {
      selector: '[data-help="hr-employee-delete"]',
      section: "Row actions",
      optional: true,
      minRole: "superadmin",
      title: "Delete the employee",
      body: "Deletes the employee after you confirm. The same button on a dependent's line deletes the dependent. Only SuperAdmin sees this button.",
    },
    {
      selector: '[data-help="hr-enrollment-row"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Read an enrollment line",
      body: "Each line shows the package, its Valid From/To dates, Eligible From and, when set, Enrollment Date and Cancelation Date. The background shows the status: green means enrolled, grey means eligible but not enrolled, red means cancelled, dark grey means the package has expired, and a fade means a future enrollment or cancellation. A dashed green border means an enrollment is waiting for approval, and a dashed red border means a cancellation is waiting for approval.",
    },
    {
      selector: '[data-help="hr-enrollment-approve"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Approve an enrollment or cancellation",
      body: "The green button appears only on a line with a pending request, and its tooltip says Approve Enrollment or Approve Cancellation. For an enrollment, you confirm a Date of Approval (the requested enrollment date is filled in), and that date becomes the enrollment date. This needs edit rights: with a read-only role the change is refused.",
    },
    {
      selector: '[data-help="hr-enrollment-reject"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Reject an enrollment or cancellation",
      body: "The red button rejects the pending request and asks for a Rejection Comment. Rejecting an enrollment removes its enrollment date, and if the enrollment was a package upgrade, the previous package is restored. Rejecting a cancellation removes the cancellation date, so the enrollment continues.",
    },
    {
      selector: '[data-help="hr-enrollment-consent-documents"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Check the consent documents before deciding",
      body: "Next to the approve and reject buttons, the document button opens the consent documents attached to this enrollment, read-only. Its tooltip shows how many documents there are.",
    },
    {
      selector: '[data-help="hr-enrollment-age-warning"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Understand an age-limit warning",
      body: "A red calendar icon means the person's age is outside the package's age limits. Hover over it to see the age and the limits.",
    },
    {
      selector: '[data-help="hr-enrollment-rejection-comment"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Read why a request was rejected",
      body: "A red list icon appears on a line whose enrollment or cancellation was rejected. Hover over it to read the rejection comment.",
    },
    {
      selector: '[data-help="hr-enrollment-edit"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Edit the enrollment",
      body: "Opens Edit Enrollment for this line, for example to set or correct the enrollment or cancellation dates. Read-only users see the button disabled.",
    },
    {
      selector: '[data-help="hr-enrollment-delete"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Delete the enrollment line",
      body: "Deletes this enrollment line after you confirm. Read-only users see the button disabled.",
    },
    {
      selector: '[data-help="hr-dependent-row"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Review a dependent",
      body: "Dependents are listed under Dependents, with their enrollment lines in this package indented below them. A red warning icon marks a dependent with open items, such as a pending request or an age-limit problem.",
    },
    {
      selector: '[data-help="hr-dependent-edit"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "View or edit a dependent",
      body: "Opens the dependent's form. The icon is green for a dependent the employee added in the employee portal, and yellow for a dependent whose data the employee changed. Read-only users see the icon disabled.",
    },
  ],
};
